import type * as Common from '@piccolohealth/pbs-common';
import { ChakraV3, createColumnHelper, DataTable, Empty } from '@piccolohealth/ui';
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import { FormSection } from '../../components/forms/FormSection';
import { FormStack } from '../../components/forms/FormStack';
import { showModal } from '../../components/generic/Modal';
import { useIncidentCategoriesQuery } from '../../graphql/hooks/useIncidentCategoriesQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { IncidentCategoryTag } from '../incidents/IncidentCategoryTag';
import { IncidentCategoryCreateModal } from './incidentCategories/IncidentCategoryCreateModal';
import { IncidentCategoryDeleteModal } from './incidentCategories/IncidentCategoryDeleteModal';
import { IncidentCategoryUpdateModal } from './incidentCategories/IncidentCategoryUpdateModal';

export const IncidentCategorySettings = () => {
  const { organization } = useAppContext();

  const { isLoading, data, error } = useIncidentCategoriesQuery({
    organizationId: organization.id,
  });

  const incidentCategories = (data?.organization?.incidentCategories ??
    []) as Common.IncidentCategory[];

  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper<Common.IncidentCategory>();

    return [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (props) => <IncidentCategoryTag category={props.row.original} size='sm' />,
        minSize: 200,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        minSize: 100,
        maxSize: 120,
        cell: (props) => (
          <ChakraV3.HStack>
            <ChakraV3.Button
              onClick={() =>
                showModal(IncidentCategoryUpdateModal, {
                  category: props.row.original,
                })
              }
              size='xs'
              variant='plain'
            >
              Edit
            </ChakraV3.Button>
            <ChakraV3.Button
              onClick={() =>
                showModal(IncidentCategoryDeleteModal, {
                  categoryId: props.row.original.id,
                  categoryName: props.row.original.name,
                })
              }
              size='xs'
              variant='plain'
              color='danger'
            >
              Delete
            </ChakraV3.Button>
          </ChakraV3.HStack>
        ),
      }),
    ];
  }, []);

  return (
    <FormSection
      heading='Incident Categories'
      description='Create and manage incident categories'
      maxW='lg'
    >
      <ChakraV3.Separator />
      <FormStack gap={0} mb={6}>
        <DataTable
          isLoading={isLoading}
          error={error}
          columns={columns}
          data={incidentCategories}
          renderEmpty={() => <Empty title='No incident categories found' />}
          size='sm'
        />
        <ChakraV3.Button
          variant='subtle'
          flexShrink={0}
          size='sm'
          onClick={() => showModal(IncidentCategoryCreateModal)}
        >
          <FaPlus />
          Create new incident category
        </ChakraV3.Button>
      </FormStack>
    </FormSection>
  );
};
